<!-- 
Shows a risk factor number, intelligently working out how many decimal places are required.

If the rounded risk factor equals one of the risk factor table's lower bounds but
the unrounded risk factor does not, then this component will show the risk factor with sufficient precision
so that it's clear why it falls in a particular risk factor band.

For example:
Let's say the Net Profit Ratio risk factor table has a band ">0 to 0.7".

If the NPR is calculated to be 0.000016 then we want to show it as "0.00002" so that 
it's clear why NPR falls into the ">0 to 0.7" band.

If the NPR is calculated to be 0.100000321 then we are happy to show it as "0.10", because
the rounding will not impact on what band it is in.
-->
<template>
  <span>{{ formattedValue }}</span>
</template>

<script>
import _ from "underscore";
import * as action from "@/store/levy-collection-risk-factors/action-types";
import * as estimatorAction from "@/store/levy-estimator/action-types";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import * as formatting from "@/helpers/formatting.helper";

export default {
  mixins: [filterMixins, lookupMixins],
  props: {
    type: {
      type: String,
      required: true
    },
    levyCollectionId: {
      validator: prop => typeof prop === "number" || prop === null,
      default: null
    },
    estimatorKey: {
      validator: prop => typeof prop === "string" || prop === null,
      default: null
    },
    isPercentage: {
      type: Boolean,
      default: false
    },
    value: {
      validator: prop => typeof prop === "number" || prop === null,
      default: null
    },
    decimalPlaces: {
      type: Number,
      required: true
    }
  },
  computed: {
    requiresSpecialFormatting() {
      const rounded = formatting.round(this.value, this.decimalPlaces);
      return _(this.factors).some(f => f.lowerBound === rounded);
    },
    formattedValue() {
      let v = this.value;
      let dps = this.decimalPlaces;
      if (this.requiresSpecialFormatting) {
        dps = formatting.getDecimalPlacesForPrecision(this.value, this.decimalPlaces);
      }
      return this.isPercentage ? formatting.percentage(v, dps - 2) : formatting.numericInput(v, dps);
    },
    isEstimator() {
      return this.estimatorKey != null;
    },
    state() {
      if (this.isEstimator) {
        return this.$store.state.levyEstimator.estimator;
      } else {
        return this.$store.state.levyCollectionRiskFactors.listing;
      }
    },
    loaded() {
      return this.state.loaded;
    },
    riskFactorType() {
      return this.getLookup(this.type, this.lookups.riskFactorTypes);
    },
    factors() {
      const list = this.isEstimator ? this.state.data.riskFactors : this.state.data;
      return list.filter(r => r.type == this.type);
    }
  },
  watch: {
    levyCollectionId() {
      this.reload();
    },
    estimatorKey() {
      this.reload();
    },
    loaded() {
      this.reload();
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    reload() {
      if (this.isEstimator) {
        this.$store.dispatch(estimatorAction.VIEW_REFRESH, this.estimatorKey);
      } else if (this.levyCollectionId) {
        const isDifferentLevyCollection = this.state.levyCollectionId != this.levyCollectionId;
        if ((!this.state.loaded && !this.state.loading) || isDifferentLevyCollection) {
          this.$store.dispatch(action.VIEW, this.levyCollectionId);
        }
      }
    }
  }
};
</script>
